import React, { useEffect } from 'react';
import { PaymentChannelType, ChannelValue, ChannelsConfig } from '@/services/types';

export interface SimplePaymentSelectorProps {
  channels?: ChannelsConfig;
  selectedType?: PaymentChannelType;
  selectedChannel?: ChannelValue;
  onTypeSelect: (type: PaymentChannelType) => void;
  onChannelSelect: (channel: ChannelValue) => void;
  disabled?: boolean;
}

// 支付类型选项
const paymentTypeOptions: { value: PaymentChannelType; label: string }[] = [
  { value: 'wechat', label: '微信支付' },
  { value: 'alipay', label: '支付宝' }
];

export const SimplePaymentSelector: React.FC<SimplePaymentSelectorProps> = ({
  channels,
  selectedType,
  selectedChannel,
  onTypeSelect,
  onChannelSelect,
  disabled = false
}) => {
  // 获取当前选择支付类型的可用渠道
  const getAvailableChannels = () => {
    if (!channels || !selectedType) return [];
    // 只处理 channels 对象中存在的支付类型
    if (selectedType in channels) {
      return channels[selectedType as keyof ChannelsConfig] || [];
    }
    return [];
  };

  const availableChannels = getAvailableChannels();

  // Auto-select first payment type if none selected
  useEffect(() => {
    if (!selectedType && paymentTypeOptions.length > 0) {
      onTypeSelect(paymentTypeOptions[0].value);
    }
  }, [selectedType, onTypeSelect]);

  // Auto-select first channel when type changes or channels are available
  useEffect(() => {
    if (selectedType && !selectedChannel && availableChannels.length > 0) {
      onChannelSelect(availableChannels[0].value);
    }
  }, [selectedType, selectedChannel, availableChannels, onChannelSelect]);

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as PaymentChannelType;
    onTypeSelect(newType);
    // 重置渠道选择 - 让 App.tsx 处理
  };

  const handleChannelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newChannel = e.target.value as ChannelValue;
    onChannelSelect(newChannel);
  };

  return (
    <div className="space-y-3">
      {/* 支付类型选择 */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          支付类型
        </label>
        <select
          value={selectedType || paymentTypeOptions[0]?.value || ''}
          onChange={handleTypeChange}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          {paymentTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* 支付渠道选择 */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          支付渠道
        </label>
        <select
          value={selectedChannel || availableChannels[0]?.value || ''}
          onChange={handleChannelChange}
          disabled={disabled || !selectedType || availableChannels.length === 0}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          {availableChannels.map((channel: { name: string; value: ChannelValue }) => (
            <option key={channel.value} value={channel.value}>
              {channel.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};